import { TFunction } from "../../i18n/tt";
import { IncreaseNominalValueDTO, IssueNewSharesDTO, Transaction } from "../../types/Transactions";
import { TableColumn } from "../../components-v2/DataTable";
import { compact } from "lodash";
import { Box, VBox } from "../../components-v2/Box";
import { Icon } from "../../components-v2/Icon";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { TransactionType } from "../../types/enums/TransactionType";
import { Tag } from "../../components-v2/Tag";
import { LocaleFormat } from "../../i18n/localeFormat";
import { isInitialTransactionType } from "../../types/const/Transactions";
import { isBefore } from "../../utils/Dates";
import { InfoButton } from "../../components-v2/InfoButton";
import { Language } from "../../i18n/languages";
import { IconName } from "../../components-v2/iconMapping";

export type AutoImportSectionProps = {
  onConfirmAutoImportIsRead: () => void;
  isLoading: boolean;
  onboardingDate: string;
  country: string;
};

export function prepareTransactionLogTableColumns({
  lang,
  t,
  f,
  deleteTransaction,
  changeTransactionDate,
  changeNominalValue,
  editTransaction,
  canEdit,
  firstNotRegisteredIssueDate,
  hideUpdated,
}: {
  lang: Language;
  t: TFunction;
  f: LocaleFormat;
  deleteTransaction?: (transaction: Transaction) => void;
  changeTransactionDate?: (transaction: Transaction) => void;
  changeNominalValue?: (transaction: Transaction) => void;
  editTransaction?: (transaction: Transaction) => void;
  canEdit: boolean;
  hideUpdated?: boolean;
  firstNotRegisteredIssueDate?: string;
}): Array<TableColumn<Transaction>> {
  return compact([
    {
      name: "icon",
      label: "",
      align: "left",
      width: "10px",
      getValue: (logItem) => (
        <VBox>
          <Icon name={transactionTypeIconMapping[logItem.transactionType]} />
        </VBox>
      ),
    },
    {
      name: "transactionType",
      label: t("transactions.transaction-type"),
      align: "left",
      getValue: (logItem) => {
        const transactionType =
          logItem.transactionType === "ISSUE"
            ? "transactions.summary.issue-new-shares"
            : logItem.transactionType;
        return t(transactionType);
      },
    },
    {
      name: "nameFrom",
      label: t("transactions.description"),
      align: "left",
      getValue: (logItem) => {
        const isAfterNotRegisteredIssue =
          firstNotRegisteredIssueDate &&
          isBefore(firstNotRegisteredIssueDate, logItem.transactionDate);
        return (
          <Box justifyContent="space-between">
            <div>{logItem.descriptions.find((d) => d.language === lang)?.text}</div>
            {isNotRegisteredIssue(logItem) ? (
              <Box alignItems={"center"}>
                <Tag
                  color={"warning"}
                  label={t("transactions.not-registered-in-br")}
                  onClick={
                    canEdit && changeTransactionDate
                      ? (e) => {
                          e.stopPropagation();
                          changeTransactionDate(logItem);
                        }
                      : undefined
                  }
                  tooltip={t("transactions.not-registered-in-br.tooltip")}
                />
                <InfoButton infoText={{ key: "transactions.not-registered-in-br.info" }} />
              </Box>
            ) : (
              isAfterNotRegisteredIssue && (
                <Box>
                  <Tag color={"neutral"} label={t("transactions.not-reported")} />
                  <InfoButton infoText={{ key: "transactions.not-reported.info" }} />
                </Box>
              )
            )}
            {logItem.attachments?.length > 0 && <Icon name={"paperclip"} />}
          </Box>
        );
      },
    },
    !hideUpdated && {
      name: "updated",
      label: t("transactions.last-updated"),
      align: "right",
      getValue: (logItem) => logItem.updatedDate && f.formatDate(logItem.updatedDate),
    },
    {
      name: "transactionDate",
      label: t("transactions.dateFull"),
      align: "right",
      getValue: (logItem) => logItem.transactionDate && f.formatDate(logItem.transactionDate),
    },
    canEdit && (deleteTransaction || changeTransactionDate || changeNominalValue)
      ? {
          name: "buttons",
          align: "right",
          label: "",
          getValue: (transaction) => (
            <DropdownMenu
              button={{ type: "dotdotdot" }}
              alignment={"end"}
              items={compact([
                changeNominalValue &&
                  isInitialTransactionType(TransactionType[transaction.transactionType]) && {
                    type: "item",
                    label: t("transactions.change-nominal-value"),
                    onSelect: () => changeNominalValue(transaction),
                    icon: "edit",
                  },
                editTransaction &&
                  transactionTypeSupportingEdit.includes(transaction.transactionType) && {
                    type: "item",
                    label: { key: "transactions.edit" },
                    onSelect: () => editTransaction(transaction),
                    icon: "edit",
                  },
                changeTransactionDate && {
                  type: "item",
                  label: t(
                    isNotRegisteredIssue(transaction)
                      ? "transactions.enter-registration-date"
                      : "transactions.change-date",
                  ),
                  onSelect: () => changeTransactionDate(transaction),
                  icon: "calendar",
                },
                deleteTransaction && {
                  type: "item",
                  label: t("transactions.delete"),
                  onSelect: () => deleteTransaction(transaction),
                  icon: "trash",
                  color: "error",
                },
              ])}
            />
          ),
        }
      : null,
  ]);
}

export function isNotRegisteredIssue(logItem: Transaction) {
  if (logItem.transactionType === "ISSUE") {
    const data = logItem.payload as IssueNewSharesDTO["data"];
    return data.isPending === true;
  } else if (logItem.transactionType === "INCREASE_NOMINAL_VALUE") {
    const data = logItem.payload as IncreaseNominalValueDTO["data"];
    return data.isPending === true;
  }
  return false;
}

export const transactionTypeIconMapping: Record<TransactionType, IconName> = {
  [TransactionType.AUTOIMPORT]: "sparkle",
  [TransactionType.INCORPORATION]: "champagne",
  [TransactionType.ISSUE]: "file-plus",
  [TransactionType.BUY_SELL]: "money",
  [TransactionType.GIFT]: "parachute",
  [TransactionType.INHERITANCE]: "arrow-bend-double-up-right",
  [TransactionType.OTHER_TRANSFER]: "arrows-left-right",
  [TransactionType.SPLIT]: "arrows-out-line-horizontal",
  [TransactionType.REVERSE_SPLIT]: "arrows-in-line-horizontal",
  [TransactionType.INCREASE_NOMINAL_VALUE]: "arrow-fat-line-up",
  [TransactionType.CANCELLATION_OF_SHARES]: "file-minus",
  [TransactionType.REDUCTION_NOMINAL_VALUE]: "arrow-fat-line-down",
  [TransactionType.PAY_DIVIDEND]: "potted-plant",
  [TransactionType.GROUP_CONTRIBUTION]: "briefcase",
  [TransactionType.CONVERSION_OF_SHARES]: "arrow-u-up-right",
  [TransactionType.CONVERSION_FROM_ISIN]: "code-block",
  [TransactionType.SHAREHOLDING_PER_DATE]: "calendar-check",
  [TransactionType.LIQUIDATION]: "trash",
  [TransactionType.MERGER]: "unite",
};

export const transactionTypeSupportingEdit: (keyof typeof TransactionType)[] = [
  TransactionType.SPLIT,
  TransactionType.REVERSE_SPLIT,
  TransactionType.BUY_SELL,
];
